import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Sparkles, Flame, BookOpen, Camera, MessageCircle, Brain, Mic, TrendingUp, TrendingDown, CalendarCheck, Share2, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import AppLayout from "@/components/AppLayout";
import { langName } from "@/lib/constants";
import { generateRecapCard } from "@/lib/shareCard";

export default function Recap() {
  const { user } = useAuth();
  const [d, setD] = useState(null);
  const [sharing, setSharing] = useState(false);
  useEffect(() => { api.get("/recap").then((r) => setD(r.data)).catch(() => {}); }, []);

  const share = useCallback(async () => {
    if (!d || sharing) return;
    setSharing(true);
    try {
      const blob = await generateRecapCard(d, langName(d.target_lang));
      const file = new File([blob], "fluentloop-week.png", { type: "image/png" });
      const url = `${window.location.origin}/app?ref=${encodeURIComponent(user?.user_id || "guest")}`;
      const text = `My week in ${langName(d.target_lang)} with FluentLoop: ${d.words_learned} new words & a ${d.streak_count}-day streak 🔥`;
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        try { await navigator.share({ files: [file], title: "FluentLoop", text, url }); return; }
        catch (e) { if (e?.name === "AbortError") return; }
      }
      const dl = URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href = dl; a.download = "fluentloop-week.png"; a.click();
      URL.revokeObjectURL(dl);
      try { await navigator.clipboard.writeText(`${text} → ${url}`); } catch (e) { /* clipboard blocked */ }
      toast.success("Recap card saved & link copied — go flex! 🎉");
    } catch (e) { console.warn("recap share failed", e); toast.error("Could not build the card."); }
    finally { setSharing(false); }
  }, [d, sharing, user]);

  if (!d) return <AppLayout><div className="py-24 flex justify-center"><div className="w-12 h-12 border-4 border-ink border-t-coral rounded-full animate-spin" /></div></AppLayout>;

  const stats = [
    { icon: BookOpen, label: "words learned", value: d.words_learned, bg: "bg-mint" },
    { icon: CalendarCheck, label: "active days", value: `${d.active_days}/7`, bg: "bg-butter" },
    { icon: Camera, label: "captures", value: d.captures, bg: "bg-sky" },
    { icon: MessageCircle, label: "conversations", value: d.conversations, bg: "bg-lavender" },
    { icon: Brain, label: "quizzes", value: d.quizzes, bg: "bg-peach" },
    { icon: Flame, label: "day streak", value: d.streak_count, bg: "bg-butter" },
  ];

  return (
    <AppLayout>
      <div className="max-w-2xl mx-auto px-4 py-8">
        <div className="flex items-center gap-2"><Sparkles className="w-6 h-6 text-coral" /><h1 className="font-cabinet font-extrabold text-3xl">Your week in review</h1></div>
        <p className="text-gray-600">Look what you did in {langName(d.target_lang)} this week 🎉</p>

        <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }}
          className="nb-card bg-ink text-white p-6 mt-6 text-center shadow-hard-lg">
          <div className="text-sm font-bold uppercase tracking-widest text-mint">This week</div>
          <div className="font-cabinet font-extrabold text-6xl mt-2">{d.words_learned}</div>
          <div className="font-bold">new words mastered</div>
          {d.accent_avg != null && (
            <div className="inline-flex items-center gap-2 mt-4 nb-tag bg-white text-ink">
              <Mic className="w-4 h-4" /> Accent {d.accent_avg}
              {d.accent_delta != null && d.accent_delta !== 0 && (
                <span className={`flex items-center gap-0.5 ${d.accent_delta > 0 ? "text-green-600" : "text-coral"}`}>
                  {d.accent_delta > 0 ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
                  {Math.abs(d.accent_delta)}
                </span>
              )}
            </div>
          )}
        </motion.div>

        <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mt-6">
          {stats.map((s, i) => (
            <motion.div key={s.label} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}
              className={`nb-card ${s.bg} p-5`}>
              <s.icon className="w-6 h-6" />
              <div className="font-cabinet font-extrabold text-3xl mt-2">{s.value}</div>
              <div className="font-bold text-xs">{s.label}</div>
            </motion.div>
          ))}
        </div>

        {d.top_words?.length > 0 && (
          <div className="nb-card p-5 mt-6">
            <h3 className="font-cabinet font-bold text-lg">Fresh in your vocab bank</h3>
            <div className="flex flex-wrap gap-2 mt-3">
              {d.top_words.map((w, i) => (
                <span key={`${w.word}-${i}`} className="nb-tag bg-mint/50">{w.word} · {w.translation}</span>
              ))}
            </div>
          </div>
        )}

        <Link to="/today" className="nb-btn-primary w-full mt-6 py-4 text-lg">Keep the momentum → Today's Loop</Link>
        <button onClick={share} disabled={sharing} className="nb-btn-outline w-full mt-3 py-3" data-testid="recap-share-btn">
          {sharing ? <RefreshCw className="w-5 h-5 animate-spin" /> : <Share2 className="w-5 h-5" />} {sharing ? "Building card…" : "Share my week"}
        </button>
      </div>
    </AppLayout>
  );
}
